<template>
  <div class="border border-[#E5E5E5] rounded-2xl bg-white shadow-sm overflow-hidden">
    <div class="bg-[#FFF5F0] p-[1rem] border-b border-[#FFE4D6]">
      <h2 class="font-semibold text-xl text-black m-0">
        Alur Eksekusi
      </h2>
    </div>

    <div class="p-[1.5rem] flex flex-col gap-y-[.5rem]">
      <div
        v-for="(step, index) in executionFlow"
        :key="step.code || index"
        class="flex items-start relative gap-x-[.75rem]"
      >
        <!-- Dot & Timeline Line -->
        <div class="flex flex-col items-center self-stretch">
          <div
            v-if="step.status === 'COMPLETED'"
            class="w-3 h-3 rounded-full bg-[#30A46C] shrink-0"
          />
          <div
            v-else-if="index === activeIndex && step.status !== 'CANCELLED'"
            class="dot-icon shrink-0"
          />
          <div
            v-else
            class="w-3 h-3 rounded-full bg-[#737373] shrink-0"
          />
          <div
            v-if="index < executionFlow.length - 1"
            class="w-px bg-[#E5E5E5] grow mt-[.5rem]"
          />
        </div>

        <!-- Content -->
        <div class="w-full pb-[.5rem]">
          <!-- Title & Top Right Info -->
          <div class="flex items-start justify-between gap-[.5rem] mb-[.25rem]">
            <div>
              <div
                class="font-medium text-sm text-black/40"
                :class="{ 'font-semibold text-black': (index === activeIndex && data.status.code !== 'CANCELLED') || step.status === 'COMPLETED' }"
              >
                {{ getStepTitle(step) }}
              </div>
              <!-- Description / Subtitle -->
              <div
                v-if="getStepDescription(step)"
                class="text-xs text-[#737373]/40 mb-[.5rem]"
                :class="{ '!text-[#737373]': index === activeIndex || step.status === 'COMPLETED' }"
              >
                {{ getStepDescription(step) }}
              </div>
            </div>

            <div>
              <!-- Timestamp for REQUEST_SUBMITTED when COMPLETED -->
              <div
                v-if="step.code === 'REQUEST_SUBMITTED' && step.status === 'COMPLETED' && step.occurred_at"
                class="text-right text-xs text-[#737373] leading-tight"
              >
                <div class="text-black">
                  {{ formatDate(step.occurred_at) }}
                </div>
                <div>{{ formatTime(step.occurred_at) }}</div>
              </div>

              <!-- Lunas Badge for WAITING_INVOICE_PAYMENT when COMPLETED -->
              <span
                v-else-if="step.code === 'WAITING_INVOICE_PAYMENT' && step.status === 'COMPLETED'"
                class="badge-status-invoice badge-invoice--lunas"
              >
                Lunas
              </span>
            </div>
          </div>

          <!-- Step Actions -->
          <!-- Step 1: REQUEST_SUBMITTED Active -->
          <template v-if="step.code === 'REQUEST_SUBMITTED' && step.status !== 'CANCELLED' && index === activeIndex">
            <BButton
              variant="primary"
              block
              :disabled="isLoadingApprove"
              @click="handleApprove"
            >
              <BSpinner
                v-if="isLoadingApprove"
                small
                class="me-1"
              />
              Setuju
            </BButton>
          </template>

          <!-- Step 2: INVOICE_PROCESSING Active -->
          <template v-else-if="step.code === 'INVOICE_PROCESSING' && index === activeIndex">
            <BDropdown
              variant="primary"
              block
              no-caret
              menu-class="w-full"
            >
              <template #button-content>
                <div class="flex items-center justify-between w-full">
                  <span class="mb-0 text-left">Buat Invoice</span>
                  <feather-icon
                    icon="ChevronDownIcon"
                    size="16"
                  />
                </div>
              </template>
              <BDropdownItem
                v-for="act in Object.values(INVOICE_ACTION)"
                :key="act"
                @click="$emit('open-invoice', act)"
              >
                {{ act }}
              </BDropdownItem>
            </BDropdown>
          </template>

          <!-- Step 2: INVOICE_PROCESSING Completed -->
          <template v-else-if="step.code === 'INVOICE_PROCESSING' && step.status === 'COMPLETED' && executionFlow[index+1].status === 'PENDING'">
            <BButton
              disabled
              block
              class="!bg-[#FFB596] !border-0 text-white font-medium cursor-not-allowed opacity-100"
            >
              Invoice diajukan
            </BButton>
          </template>

          <!-- Step 4: WAITING_DOCUMENT_COMPLETION Active -->
          <template v-else-if="step.code === 'WAITING_DOCUMENT_COMPLETION' && index === activeIndex">
            <BButton
              v-if="requestTypeCode === 'SWITCH_PARTNER'"
              variant="primary"
              block
              @click="$emit('open-churn', CHURN_ACTION.EDIT_PARTNER)"
            >
              Ganti Partner
            </BButton>
            <BButton
              v-else-if="requestTypeCode === 'SWITCH_TALENT'"
              variant="primary"
              block
              @click="$emit('open-churn', CHURN_ACTION.EDIT_TALENT)"
            >
              Ganti Talent
            </BButton>
            <BButton
              v-else-if="requestTypeCode === 'OFF_TALENT'"
              variant="primary"
              block
              @click="$emit('off-talent', CHURN_ACTION.OFF_TALENT)"
            >
              Eksekusi Off Talent
            </BButton>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.dot-icon {
  width: 12px;
  height: 12px;
  background-color: #F95031;
  border-radius: 50%;
  border: 1.5px solid #FFFFFF;
  box-shadow: 0 0 0 1px #F95031;
}

.badge-status-invoice {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  white-space: nowrap;
}

.badge-invoice--lunas {
  background: #D6F1DF;
  color: #30A46C;
}
</style>

<script>
import { komtimAxiosIns } from '@/libs/axios'
import { DDMMYYYY, TIME } from '@/libs/filterDate'
import {
  INVOICE_ACTION,
  CHURN_ACTION,
  showSuccess,
  showError,
} from '../../config'

export default {
  name: 'ExecutionFlowCard',
  props: {
    data: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      INVOICE_ACTION,
      CHURN_ACTION,
      isLoadingApprove: false,
    }
  },
  computed: {
    requestTypeCode() {
      return (this.data && this.data.request_type && this.data.request_type.code) || ''
    },
    executionFlow() {
      if (Array.isArray(this.data && this.data.execution_flow) && this.data.execution_flow.length > 0) {
        return this.data.execution_flow
      }
      return [
        {
          code: 'REQUEST_SUBMITTED',
          name: 'Setujui Permintaan',
          description: 'Pastikan permintaan sudah tidak bisa diubah.',
          status: 'PENDING',
          occurred_at: null,
        },
        {
          code: 'INVOICE_PROCESSING',
          name: 'Buat Invoice',
          description: 'Hitung dan terbitkan invoice sisa hari kerja talent lama.',
          status: 'PENDING',
          occurred_at: null,
        },
        {
          code: 'WAITING_INVOICE_PAYMENT',
          name: 'Tunggu Pembayaran Invoice',
          description: 'Menunggu invoice dibayarkan oleh partner.',
          status: 'PENDING',
          occurred_at: null,
        },
        {
          code: 'WAITING_DOCUMENT_COMPLETION',
          name: 'Eksekusi Unassign',
          description: 'Ubah status talent pada partner.',
          status: 'PENDING',
          occurred_at: null,
        },
        {
          code: 'COMPLETED',
          name: 'Selesai',
          description: 'Semua kewajiban telah selesai.',
          status: 'PENDING',
          occurred_at: null,
        },
      ]
    },
    activeIndex() {
      const index = this.executionFlow.findIndex(step => step.status !== 'COMPLETED')
      return index !== -1 ? index : this.executionFlow.length - 1
    },
  },
  methods: {
    getStepTitle(step) {
      if (step.code === 'WAITING_DOCUMENT_COMPLETION') {
        if (this.requestTypeCode === 'SWITCH_PARTNER') return 'Ganti Partner'
        if (this.requestTypeCode === 'SWITCH_TALENT') return 'Ganti Talent'
        return step.name || 'Eksekusi Unassign'
      }
      return step.name || ''
    },
    getStepDescription(step) {
      if (step.code === 'REQUEST_SUBMITTED' && step.status === 'COMPLETED') {
        return 'Disetujui oleh Admin'
      }
      if (step.code === 'WAITING_DOCUMENT_COMPLETION') {
        if (this.requestTypeCode === 'SWITCH_PARTNER') return 'Ubah status partner pada talent'
        return step.description || 'Ubah status talent pada partner'
      }
      return step.description || ''
    },
    formatDate(dateStr) {
      if (!dateStr) return ''
      return DDMMYYYY(dateStr)
    },
    formatTime(dateStr) {
      if (!dateStr) return ''
      return `${TIME(dateStr)} WIB`
    },
    async handleApprove() {
      if (!this.data || !this.data.id) return
      try {
        this.isLoadingApprove = true
        const url = `/v1/partner_assignment_requests/${this.data.id}/approve`
        await komtimAxiosIns.put(url)
        showSuccess({ title: 'Berhasil menyetujui request!' })
        this.$emit('approve')
      } catch {
        showError({ title: 'Gagal menyetujui request!' })
      } finally {
        this.isLoadingApprove = false
      }
    },
  },
}
</script>
